import Color from 'color'
import { ThemeType } from '@/lib/enums/ThemeType'
import { isColorDark } from '@/lib/utils'
import { colorSchema } from '@/lib/services/stores/stores.schema'

export type ThemeColorField =
  | 'background'
  | 'foreground'
  | 'primary'
  | 'primaryForeground'
  | 'secondary'
  | 'secondaryForeground'
  | 'accentSecondary'
  | 'accentSecondaryForeground'

export type ThemeColorValues = Partial<Record<ThemeColorField, string | null>>

export type ThemeProperties = ThemeColorValues &
  Partial<Record<'builder_mode' | 'smart_mode' | 'smart_accent', string | null>>

export type ThemeOption = {
  id?: string | number | null
  properties?: ThemeProperties | null
}

export type ThemeData = ThemeColorValues & {
  id?: string | number | null
  type?: string
  name?: string
  isBackgroundDark?: boolean
  isPrimaryDark?: boolean
  isSecondaryDark?: boolean
  builder_mode?: string
  smart_mode?: string
  smart_accent?: string
}

export type SmartTheme = {
  name: string
  colors: Record<ThemeColorField, string>
}

// Keep this aligned with the storefront --muted-foreground color-mix rule.
export const MUTED_FOREGROUND_WEIGHT = 0.7

/**
 * Default theme values for the theme builder
 * This is the single source of truth for all default theme settings
 */
export const DEFAULT_THEME_VALUES = {
  id: '',
  type: ThemeType.Preset.Value,
  background: '',
  foreground: '',
  primary: '',
  primaryForeground: '',
  secondary: '',
  secondaryForeground: '',
  accentSecondary: '',
  accentSecondaryForeground: '',
  isBackgroundDark: false,
  isPrimaryDark: false,
  isSecondaryDark: false,
  builder_mode: 'auto',
  smart_mode: 'soft',
  smart_accent: 'standard',
}

export function normalizeHexColor<T>(color: T): T | string {
  if (typeof color !== 'string') {
    return color
  }

  const normalized = color.trim()

  if (normalized.startsWith('#')) {
    return normalized.toLowerCase()
  }

  if (/^[0-9a-f]{6}$/i.test(normalized)) {
    return `#${normalized.toLowerCase()}`
  }

  return normalized
}

export function normalizeThemeColors<T extends ThemeColorValues>(colors: T): T {
  if (!colors || typeof colors !== 'object') return colors

  const colorFields: ThemeColorField[] = [
    'background',
    'foreground',
    'primary',
    'primaryForeground',
    'secondary',
    'secondaryForeground',
    'accentSecondary',
    'accentSecondaryForeground',
  ]

  const normalized = { ...colors }
  const normalizedColors: ThemeColorValues = {}
  for (const field of colorFields) {
    const value = normalized[field]
    if (value) {
      normalizedColors[field] = normalizeHexColor(value)
    }
  }
  return { ...normalized, ...normalizedColors }
}

/** Initializes theme data with default values for any missing builder settings. */
export function initializeThemeWithDefaults(themeData: ThemeData = {}) {
  return {
    ...DEFAULT_THEME_VALUES,
    ...themeData,
  }
}

/** Validates a color string with the shared color schema; non-strings are invalid. */
export function isColorValid(color: unknown): boolean {
  if (!color || typeof color !== 'string') {
    return false
  }
  return colorSchema.safeParse(normalizeHexColor(color)).success
}

/** Builds the storefront's derived muted text color from a theme pair. */
export function getMutedForegroundColor(backgroundColor: string, foregroundColor: string) {
  const background = Color(normalizeHexColor(backgroundColor))
  const foreground = Color(normalizeHexColor(foregroundColor))

  return foreground
    .mix(background, 1 - MUTED_FOREGROUND_WEIGHT)
    .hex()
    .toLowerCase()
}

/** Returns the derived muted text's contrast against its theme background. */
export function getMutedForegroundContrast(backgroundColor: string, foregroundColor: string) {
  const background = Color(normalizeHexColor(backgroundColor))

  return background.contrast(Color(getMutedForegroundColor(backgroundColor, foregroundColor)))
}

/** Generates accessible foreground suggestions for a given background color. */
export function getAccessibleColors(
  backgroundColor: string,
  { requireMutedContrast = false }: { requireMutedContrast?: boolean } = {}
): string[] {
  try {
    const bg = Color(normalizeHexColor(backgroundColor))
    const suggestions: string[] = []

    // 1. Black or White (Best contrast)
    const white = Color('#ffffff')
    const black = Color('#000000')
    const whiteContrast = bg.contrast(white)
    const blackContrast = bg.contrast(black)

    suggestions.push(whiteContrast > blackContrast ? '#ffffff' : '#000000')

    // 2. Monochrome (Lighten/Darken background)
    let monochrome = bg
    const step = 0.1
    let foundMono = false
    for (let i = 0; i < 10; i++) {
      if (bg.isDark()) {
        monochrome = monochrome.lighten(step)
      } else {
        monochrome = monochrome.darken(step)
      }
      if (bg.contrast(monochrome) >= 4.5) {
        suggestions.push(monochrome.hex())
        foundMono = true
        break
      }
    }
    // Fallback if simple lightening/darkening didn't work (e.g. extremely saturated mid-tones)
    if (!foundMono) {
      suggestions.push(bg.isDark() ? '#ffffff' : '#000000')
    }

    // 3. Complementary (Rotate hue, then adjust lightness)
    let comp = bg.rotate(180)
    if (bg.contrast(comp) < 4.5) {
      for (let i = 0; i < 10; i++) {
        if (bg.isDark()) {
          comp = comp.lighten(step)
        } else {
          comp = comp.darken(step)
        }
        if (bg.contrast(comp) >= 4.5) {
          break
        }
      }
    }
    suggestions.push(comp.hex())

    // Deduplicate and filter invalid
    return [...new Set(suggestions.map((c) => c?.toLowerCase()))].filter((c) => {
      if (Color(c).contrast(bg) < 4.5) {
        return false
      }

      return !requireMutedContrast || getMutedForegroundContrast(backgroundColor, c) >= 4.5
    })
  } catch {
    return []
  }
}

/**
 * Color descriptions for theme colors
 */
export const COLOR_DESCRIPTIONS = {
  background: 'Main background color for your store',
  foreground: 'Primary text color',
  primary: 'Brand color for buttons and highlights',
  primaryForeground: 'Text color on primary elements',
  secondary: 'Secondary background color for cards and sections',
  secondaryForeground: 'Text color on secondary elements',
  accentSecondary: 'Tertiary accent color for subtle highlights',
  accentSecondaryForeground: 'Text color on accent elements',
}

/** Builds theme data from a theme option, falling back to the builder defaults. */
export function buildThemeData(
  option: ThemeOption | null | undefined,
  type: string = ThemeType.Preset.Value
) {
  const properties: ThemeProperties = option?.properties || {}
  const background = properties.background
  const primary = properties.primary
  const secondary = properties.secondary

  const themeData: ThemeData = {
    type,
    id: option?.id,
    name: 'Custom', // Always use "Custom" for the main theme name
    background,
    foreground: properties.foreground,
    primary,
    primaryForeground: properties.primaryForeground,
    secondary,
    secondaryForeground: properties.secondaryForeground,
    accentSecondary: properties.accentSecondary,
    accentSecondaryForeground: properties.accentSecondaryForeground,
    isBackgroundDark: isColorDark(background),
    isPrimaryDark: isColorDark(primary),
    isSecondaryDark: isColorDark(secondary),
    // Always include builder settings with defaults
    builder_mode: properties.builder_mode || DEFAULT_THEME_VALUES.builder_mode,
    smart_mode: properties.smart_mode || DEFAULT_THEME_VALUES.smart_mode,
    smart_accent: properties.smart_accent || DEFAULT_THEME_VALUES.smart_accent,
  }

  // Always merge with defaults for consistency, then normalize colors
  const merged = initializeThemeWithDefaults(themeData)
  return normalizeThemeColors(merged)
}

/**
 * Generates a single smart color theme from a brand hex color, a mode ('soft', 'light', or
 * 'dark'), and an accent style ('standard' or 'complementary'). Returns null when the brand
 * color cannot be parsed or the mode is unknown.
 */
export function generateSmartTheme(
  brandColor: string,
  mode: string,
  accent: string
): SmartTheme | null {
  try {
    const brand = Color(brandColor)
    const white = Color('#ffffff')
    const black = Color('#000000')

    // Helper to ensure text contrast
    const getContrastText = (bg: string) => {
      const c = Color(bg)
      return c.contrast(white) > c.contrast(black) ? '#ffffff' : '#000000'
    }

    // Pre-compute brand colors
    const brandHex = brand.hex()
    const brandRotated = brand.rotate(180)
    const primaryForeground = getContrastText(brandHex)

    // Generate theme based on mode
    if (mode === 'light') {
      // Light theme colors
      const brandAlpha01 = brand.alpha(0.1).hex()
      const brandRotatedAlpha01 = brandRotated.alpha(0.1).hex()
      const brandAlpha01Foreground = getContrastText(brandAlpha01)
      const brandRotatedAlpha01Foreground = getContrastText(brandRotatedAlpha01)

      return {
        name: accent === 'complementary' ? 'Light Comp' : 'Light',
        colors: normalizeThemeColors({
          background: '#ffffff',
          foreground: '#0f172a',
          primary: brandHex,
          primaryForeground,
          secondary: '#f1f5f9',
          secondaryForeground: '#0f172a',
          accentSecondary: accent === 'complementary' ? brandRotatedAlpha01 : brandAlpha01,
          accentSecondaryForeground:
            accent === 'complementary' ? brandRotatedAlpha01Foreground : brandAlpha01Foreground,
        }),
      }
    }

    if (mode === 'soft') {
      // Soft theme colors
      const brandL98 = brand.lightness(98).hex()
      const brandL92 = brand.lightness(92).hex()
      const brandL85 = brand.lightness(85).hex()
      const brandRotatedL85 = brandRotated.lightness(85).hex()

      const brandL98Foreground = getContrastText(brandL98)
      const brandL92Foreground = getContrastText(brandL92)
      const brandL85Foreground = getContrastText(brandL85)
      const brandRotatedL85Foreground = getContrastText(brandRotatedL85)

      return {
        name: accent === 'complementary' ? 'Soft Comp' : 'Soft',
        colors: normalizeThemeColors({
          background: brandL98,
          foreground: brandL98Foreground,
          primary: brandHex,
          primaryForeground,
          secondary: brandL92,
          secondaryForeground: brandL92Foreground,
          accentSecondary: accent === 'complementary' ? brandRotatedL85 : brandL85,
          accentSecondaryForeground:
            accent === 'complementary' ? brandRotatedL85Foreground : brandL85Foreground,
        }),
      }
    }

    if (mode === 'dark') {
      // Dark theme colors - based on brand color
      // Check if brand is grayscale (low saturation) to avoid introducing unwanted hue
      const isGrayscale = brand.saturationl() < 10
      const darkBackground = isGrayscale
        ? Color('#0f1115').hex()
        : brand.saturationl(30).lightness(8).hex()
      const darkSecondary = isGrayscale
        ? Color('#1c1f26').hex()
        : brand.saturationl(25).lightness(15).hex()
      const darkAccent = isGrayscale
        ? Color('#2d3139').hex()
        : brand.saturationl(40).lightness(25).hex()
      const darkRotatedAccent = isGrayscale
        ? Color('#3d424d').hex()
        : brandRotated.saturationl(40).lightness(25).hex()

      const darkBackgroundForeground = getContrastText(darkBackground)
      const darkSecondaryForeground = getContrastText(darkSecondary)
      const darkAccentForeground = getContrastText(darkAccent)
      const darkRotatedAccentForeground = getContrastText(darkRotatedAccent)

      return {
        name: accent === 'complementary' ? 'Dark Comp' : 'Dark',
        colors: normalizeThemeColors({
          background: darkBackground,
          foreground: darkBackgroundForeground,
          primary: brandHex,
          primaryForeground,
          secondary: darkSecondary,
          secondaryForeground: darkSecondaryForeground,
          accentSecondary: accent === 'complementary' ? darkRotatedAccent : darkAccent,
          accentSecondaryForeground:
            accent === 'complementary' ? darkRotatedAccentForeground : darkAccentForeground,
        }),
      }
    }

    return null
  } catch {
    return null
  }
}
